/**
 * Application Error Boundary
 * Catches unhandled React rendering errors and displays a recovery UI.
 * Wraps the entire app tree — sits above all providers except Suspense.
 * Falls back to a translated error message with a retry button.
 */

import { Box, Button, Typography } from "@mui/material";
import { Component, ErrorInfo, ReactNode } from "react";

import { i18nGlobal } from "@/i18n";
import { loggerClient } from "@utils/logger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    loggerClient.error("ErrorBoundary caught", error, {
      componentStack: errorInfo.componentStack
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const t = (key: string, fallback: string) => i18nGlobal.t(key, fallback);

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          gap={2}
          p={4}
          role="alert"
        >
          <Typography variant="h2Bold" color="error">
            {t("eInvoicingApp.errorBoundary.title", "Something went wrong")}
          </Typography>
          <Typography variant="bodyText" color="text.secondary">
            {this.state.error?.message ||
              t(
                "eInvoicingApp.errorBoundary.defaultMessage",
                "An unexpected error occurred"
              )}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleReset}
          >
            {t("eInvoicingApp.tryAgain", "Try Again")}
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
